/**
 * GH Slideshow JavaScript
 * Handles the slideshow functionality for the GH-Slideshow section
 * Fixed for proper height calculation and live website behavior
 */

class GHSlideshow {
    constructor(element) {
        this.element = element;
        this.slides = element.querySelector('.gh-slideshow__slides');
        this.flickityInstance = null;
        // Store reference to this instance on the element
        this.element.ghSlideshow = this;
        this.init();
    }

    init() {
        if (!this.slides) return;

        // Wait for images to load before initializing
        this.waitForImages().then(() => {
            this.initFlickity();
        });
    }

    waitForImages() {
        return new Promise((resolve) => {
            const images = this.slides.querySelectorAll('img');
            if (images.length === 0) {
                resolve();
                return;
            }

            let loadedImages = 0;
            const totalImages = images.length;

            const imageLoaded = () => {
                loadedImages++;
                if (loadedImages === totalImages) {
                    resolve();
                }
            };

            images.forEach(img => {
                if (img.complete) {
                    imageLoaded();
                } else {
                    img.addEventListener('load', imageLoaded);
                    img.addEventListener('error', imageLoaded);
                }
            });

            // Fallback timeout
            setTimeout(resolve, 3000);
        });
    }

    initFlickity() {
        if (this.slides.classList.contains('flickity-enabled')) {
            return;
        }

        const autoPlay = this.element.dataset.autoRotate === 'true';
        const slideSpeed = parseInt(this.element.dataset.slideSpeed) || 5000;
        const wrapAround = this.element.dataset.wrapAround === 'true';

        // Flickity options - optimized for height issues and custom navigation
        const flickityOptions = {
            cellAlign: 'left',
            contain: true,
            groupCells: false,
            pageDots: false,
            prevNextButtons: false, // Disabled - using custom button
            wrapAround: wrapAround,
            autoPlay: autoPlay ? slideSpeed : false,
            pauseAutoPlayOnHover: true,
            adaptiveHeight: false,
            setGallerySize: false, // Important: prevents Flickity from setting height to 0
            imagesLoaded: true,
            percentPosition: false,
            resize: true
        };

        // Initialize Flickity
        if (typeof Flickity !== 'undefined') {
            this.flickityInstance = new Flickity(this.slides, flickityOptions);

            // Force height after initialization
            this.forceCorrectHeight();

            // Setup custom next button
            this.setupCustomNavigation();

            // Re-force height on resize
            this.flickityInstance.on('resize', () => {
                this.forceCorrectHeight();
            });

        } else {
            console.warn('Flickity is not loaded. GH Slideshow will not function properly.');
        }
    }

    setupCustomNavigation() {
        // Wait a bit to ensure DOM is fully ready
        setTimeout(() => {
            const nextBtn = this.element.querySelector('.icon-next button');

            if (nextBtn && this.flickityInstance) {
                // Remove any existing listeners
                nextBtn.removeEventListener('click', this.handleNextClick);

                // Create bound function for cleanup
                this.handleNextClick = () => {
                    console.log('Next button clicked!'); // Debug log
                    this.flickityInstance.next(true);
                };

                // Add event listener
                nextBtn.addEventListener('click', this.handleNextClick);

                console.log('Event listener added to next button'); // Debug log
            } else {
                console.warn('Next button or Flickity instance not found');
            }
        }, 100);
    }

    forceCorrectHeight() {
        // Force the correct height to prevent 0px height issue
        const viewport = this.slides.querySelector('.flickity-viewport');
        const slider = this.slides.querySelector('.flickity-slider');

        if (viewport && slider) {
            // Get expected height from CSS
            const computedStyle = window.getComputedStyle(this.slides);
            const expectedHeight = computedStyle.height;

            if (expectedHeight && expectedHeight !== '0px') {
                viewport.style.height = expectedHeight;
                slider.style.height = expectedHeight;
            } else {
                // Fallback heights based on screen size
                const screenWidth = window.innerWidth;
                let fallbackHeight;

                if (screenWidth <= 480) {
                    fallbackHeight = '250px';
                } else if (screenWidth <= 768) {
                    fallbackHeight = '300px';
                } else if (screenWidth <= 990) {
                    fallbackHeight = '350px';
                } else {
                    fallbackHeight = '400px';
                }

                viewport.style.height = fallbackHeight;
                slider.style.height = fallbackHeight;
            }
        }
    }

    destroy() {
        // Remove event listener
        const nextBtn = this.element.querySelector('.icon-next button');
        if (nextBtn && this.handleNextClick) {
            nextBtn.removeEventListener('click', this.handleNextClick);
        }

        if (this.flickityInstance) {
            this.flickityInstance.destroy();
            this.flickityInstance = null;
        }

        // Clear reference
        this.element.ghSlideshow = null;
    }
}

// Initialize all GH Slideshows when DOM is loaded
document.addEventListener('DOMContentLoaded', function () {
    console.log('DOM loaded, initializing GH Slideshows');
    const slideshows = document.querySelectorAll('.gh-slideshow');
    console.log('Found', slideshows.length, 'slideshows');
    slideshows.forEach((slideshow, index) => {
        console.log('Initializing slideshow', index);
        new GHSlideshow(slideshow);
    });
});

// Also try immediate initialization in case DOMContentLoaded already fired
if (document.readyState === 'loading') {
    // DOM is still loading
} else {
    // DOM is already loaded
    console.log('DOM already loaded, initializing GH Slideshows immediately');
    const slideshows = document.querySelectorAll('.gh-slideshow');
    slideshows.forEach(slideshow => {
        if (!slideshow.ghSlideshow) {
            new GHSlideshow(slideshow);
        }
    });
}

// Reinitialize on section updates (for theme editor)
document.addEventListener('shopify:section:load', function (event) {
    if (event.target.classList.contains('gh-slideshow')) {
        new GHSlideshow(event.target);
    }
});

// Handle section unload
document.addEventListener('shopify:section:unload', function (event) {
    if (event.target.classList.contains('gh-slideshow')) {
        const slideshow = event.target.ghSlideshow;
        if (slideshow) {
            slideshow.destroy();
        }
    }
});

// Handle window resize
window.addEventListener('resize', function () {
    const slideshows = document.querySelectorAll('.gh-slideshow');
    slideshows.forEach(slideshowElement => {
        const slideshow = slideshowElement.ghSlideshow;
        if (slideshow && slideshow.flickityInstance) {
            setTimeout(() => {
                slideshow.forceCorrectHeight();
                slideshow.flickityInstance.resize();
            }, 100);
        }
    });
});
